import { useEffect, useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  loanSchedule,
  totalInterest,
  totalPrincipal,
  toKr,
  type LoanType,
  type ScheduleRow,
} from '@heivol-finance/calc';
import { loansApi, type LoansData, type LoanRow, type LoanKind, type LoanLedgerEntry } from '../lib/data.js';
import { formatOre } from '../lib/format.js';
import { informalBalance, scheduleDateLabel, totalOutstanding } from '../lib/loan.js';
import { chartColors } from '../lib/chart.js';
import {
  PageHeader,
  CollapsibleCard,
  StatCard,
  Button,
  DeleteButton,
  TextField,
  OreField,
  NumberField,
  Select,
  EmptyState,
} from '../components/ui.js';
import { SectionBar, Tr, Th, Td, TdNum } from '../components/table.js';

const TYPE_OPTS: { value: LoanKind; label: string }[] = [
  { value: 'annuity', label: 'Annuity loan' },
  { value: 'serial', label: 'Serial loan' },
  { value: 'interest_only', label: 'Interest-only' },
  { value: 'informal', label: 'Informal (manual)' },
];
const typeLabel = (t: LoanKind) => TYPE_OPTS.find((o) => o.value === t)?.label ?? t;
const isFormal = (t: LoanKind): t is LoanType => t !== 'informal';

export function Loans(): JSX.Element {
  const [data, setData] = useState<LoansData | null>(null);
  const reload = () => loansApi.load().then(setData);
  useEffect(() => {
    void reload();
  }, []);

  const colors = useMemo(() => chartColors(), [data]);

  const entriesByLoan = useMemo(() => {
    const map = new Map<string, LoanLedgerEntry[]>();
    for (const e of data?.entries ?? []) {
      const list = map.get(e.loan_id) ?? [];
      list.push(e);
      map.set(e.loan_id, list);
    }
    return map;
  }, [data]);

  const totalDebt = useMemo(
    () => (data ? totalOutstanding(data.loans, entriesByLoan) : 0),
    [data, entriesByLoan],
  );

  if (!data) return <PageHeader title="Loans" subtitle="Loading…" />;

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Loans"
        subtitle={
          <>
            Formal loans with an amortization schedule and informal loans as a running balance.
            Reproduces the spreadsheet's <span className="font-mono">Loans</span> tab.
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total debt" value={formatOre(totalDebt)} tone="chat" hint="Outstanding balance across all loans" />
        <StatCard label="Number of loans" value={String(data.loans.length)} />
      </div>

      <div className="mb-4 flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={() =>
            loansApi
              .addLoan({ name: 'New loan', type: 'annuity', principal_ore: 0, rate: 0.03, term_months: 12, start_date: '2026-01-01' })
              .then(reload)
          }
        >
          + Formal loan
        </Button>
        <Button
          variant="secondary"
          onClick={() => loansApi.addLoan({ name: 'New informal loan', type: 'informal', principal_ore: 0, rate: 0 }).then(reload)}
        >
          + Informal loan
        </Button>
      </div>

      {data.loans.length === 0 ? (
        <EmptyState>No loans yet. Add a formal loan (annuity, serial, interest-only) or an informal loan.</EmptyState>
      ) : (
        <div className="space-y-6">
          {data.loans.map((loan) =>
            isFormal(loan.type) ? (
              <FormalLoanCard key={loan.id} loan={loan} colors={colors} reload={reload} />
            ) : (
              <InformalLoanCard key={loan.id} loan={loan} entries={entriesByLoan.get(loan.id) ?? []} reload={reload} />
            ),
          )}
        </div>
      )}

      <p className="mt-6 text-hv-xs text-hv-text-tertiary">
        The interest portion of a payment is <span className="text-hv-warning-text">consumptive</span> (subtracted from net worth); the principal is
        net-worth-neutral (debt down, cash down). See the net-worth taxonomy.
      </p>
    </div>
  );
}

// ── Formal loan (annuity / serial / interest-only) ──────────────────────────────
function FormalLoanCard({
  loan,
  colors,
  reload,
}: {
  loan: LoanRow;
  colors: ReturnType<typeof chartColors>;
  reload: () => Promise<void>;
}): JSX.Element {
  const patch = (b: Partial<LoanRow>) => loansApi.updateLoan(loan.id, b).then(reload);

  const model = useMemo(() => {
    const periods = loan.term_months ?? 0;
    if (periods <= 0 || loan.principal_ore <= 0) {
      return { schedule: [] as ScheduleRow[], interest: 0, principal: 0 };
    }
    const monthlyRate = loan.rate / 12;
    const schedule = loanSchedule(loan.type as LoanType, loan.principal_ore, monthlyRate, periods);
    return { schedule, interest: totalInterest(schedule), principal: totalPrincipal(schedule) };
  }, [loan.type, loan.principal_ore, loan.rate, loan.term_months]);

  const series = useMemo(
    () =>
      model.schedule.map((r) => ({
        date: scheduleDateLabel(loan.start_date, r.period),
        restgaeld: toKr(r.closingBalance),
      })),
    [model.schedule, loan.start_date],
  );

  const firstPayment = model.schedule[0]?.payment ?? 0;

  return (
    <CollapsibleCard
      summary={
        <div className="flex items-center gap-2">
          <span>{loan.name}</span>
          <span className="rounded-hv-md bg-hv-ai-surface px-2 py-0.5 text-hv-xs text-hv-ai-text">{typeLabel(loan.type)}</span>
          <span className="ml-auto pl-2 tabular-nums text-hv-chat-text">{formatOre(loan.principal_ore)}</span>
        </div>
      }
      actions={<DeleteButton onClick={() => loansApi.deleteLoan(loan.id).then(reload)} label="Delete loan" />}
    >
      {/* Parameters */}
      <div className="grid grid-cols-2 gap-4 border-b border-hv-border p-4 sm:grid-cols-3 lg:grid-cols-6">
        <Labeled label="Name">
          <TextField value={loan.name} onCommit={(v) => patch({ name: v })} />
        </Labeled>
        <Labeled label="Lender">
          <TextField value={loan.lender ?? ''} onCommit={(v) => patch({ lender: v })} placeholder="e.g. Nordea" />
        </Labeled>
        <Labeled label="Type">
          <Select value={loan.type} options={TYPE_OPTS} onChange={(v) => patch({ type: v })} />
        </Labeled>
        <Labeled label="Principal">
          <OreField value={loan.principal_ore} onCommit={(v) => patch({ principal_ore: v })} />
        </Labeled>
        <Labeled label="Interest (% p.a.)">
          <NumberField value={loan.rate * 100} step={0.1} onCommit={(v) => patch({ rate: v / 100 })} suffix="%" />
        </Labeled>
        <Labeled label="Term (months)">
          <NumberField value={loan.term_months ?? 0} onCommit={(v) => patch({ term_months: Math.round(v) })} />
        </Labeled>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 border-b border-hv-border p-4 sm:grid-cols-4">
        <StatCard label="Outstanding balance now" value={formatOre(loan.principal_ore)} tone="chat" />
        <StatCard label="Payment / mo. (start)" value={formatOre(firstPayment)} tone="ai" hint={loan.type === 'serial' ? 'Falls over time' : undefined} />
        <StatCard label="Total interest" value={formatOre(model.interest)} tone="warning" hint="Consumptive" />
        <StatCard label="Total principal" value={formatOre(model.principal)} hint="Net-worth-neutral" />
      </div>

      {model.schedule.length === 0 ? (
        <div className="p-4 text-hv-sm text-hv-text-tertiary">Enter principal and term to generate the amortization schedule.</div>
      ) : (
        <>
          {/* Balance curve */}
          <div className="border-b border-hv-border p-4">
            <div className="mb-2 text-hv-sm font-medium text-hv-text">Outstanding balance over time</div>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    stroke={colors.axis}
                    tick={{ fontSize: 11 }}
                    interval={Math.max(0, Math.floor(series.length / 8) - 1)}
                  />
                  <YAxis
                    stroke={colors.axis}
                    tick={{ fontSize: 11 }}
                    width={70}
                    tickFormatter={(v: number) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
                  />
                  <Tooltip
                    formatter={(v: number) => formatOre(Math.round(v * 100))}
                    contentStyle={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, color: colors.text }}
                    labelStyle={{ color: colors.text }}
                  />
                  <Line type="monotone" dataKey="restgaeld" name="Outstanding balance" stroke={colors.chat} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Amortization schedule */}
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-hv-sm">
              <thead className="sticky top-0 bg-hv-surface">
                <Tr head>
                  <Th>Period</Th>
                  <Th>Date</Th>
                  <Th right>Opening</Th>
                  <Th right>Payment</Th>
                  <Th right>Interest</Th>
                  <Th right>Principal</Th>
                  <Th right>Closing</Th>
                </Tr>
              </thead>
              <tbody>
                {model.schedule.map((r) => (
                  <Tr key={r.period}>
                    <Td>{r.period}</Td>
                    <Td>{scheduleDateLabel(loan.start_date, r.period)}</Td>
                    <TdNum>{formatOre(r.openingBalance)}</TdNum>
                    <TdNum strong>{formatOre(r.payment)}</TdNum>
                    <TdNum>
                      <span className="text-hv-warning-text">{formatOre(r.interest)}</span>
                    </TdNum>
                    <TdNum>{formatOre(r.principal)}</TdNum>
                    <TdNum>{formatOre(r.closingBalance)}</TdNum>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </CollapsibleCard>
  );
}

// ── Informal loan (manual running-balance ledger) ───────────────────────────────
function InformalLoanCard({
  loan,
  entries,
  reload,
}: {
  loan: LoanRow;
  entries: LoanLedgerEntry[];
  reload: () => Promise<void>;
}): JSX.Element {
  const patch = (b: Partial<LoanRow>) => loansApi.updateLoan(loan.id, b).then(reload);
  const balance = informalBalance(entries);
  const lastDate = entries[entries.length - 1]?.dated ?? loan.start_date ?? '2026-01-01';

  return (
    <CollapsibleCard
      summary={
        <div className="flex items-center gap-2">
          <span>{loan.name}</span>
          <span className="rounded-hv-md bg-hv-surface-alt px-2 py-0.5 text-hv-xs text-hv-text-secondary">{typeLabel(loan.type)}</span>
          <span className={`ml-auto pl-2 tabular-nums ${balance < 0 ? 'text-hv-chat-text' : 'text-hv-text-secondary'}`}>
            {formatOre(balance)}
          </span>
        </div>
      }
      actions={<DeleteButton onClick={() => loansApi.deleteLoan(loan.id).then(reload)} label="Delete loan" />}
    >
      <div className="grid grid-cols-2 gap-4 border-b border-hv-border p-4 sm:grid-cols-4">
        <Labeled label="Name">
          <TextField value={loan.name} onCommit={(v) => patch({ name: v })} />
        </Labeled>
        <Labeled label="Lender">
          <TextField value={loan.lender ?? ''} onCommit={(v) => patch({ lender: v })} placeholder="e.g. Parents" />
        </Labeled>
        <div className="col-span-2 sm:col-span-2">
          <StatCard
            label="Current balance"
            value={formatOre(balance)}
            tone={balance < 0 ? 'chat' : 'success'}
            hint={balance < 0 ? 'Amount owed' : 'Owed to you / settled'}
          />
        </div>
      </div>

      <SectionBar
        title="Entries"
        onAdd={() =>
          loansApi
            .addEntry(loan.id, { dated: lastDate, amount_ore: 0 })
            .then(reload)
        }
      />
      <div className="overflow-x-auto">
        <table className="w-full text-hv-sm">
          <thead>
            <Tr head>
              <Th>Date</Th>
              <Th right>Amount (draw − / repayment +)</Th>
              <Th right>Running balance</Th>
              <Th />
            </Tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <Tr key={e.id}>
                <Td>
                  <TextField value={e.dated} onCommit={(v) => loansApi.updateEntry(loan.id, e.id, { dated: v }).then(reload)} />
                </Td>
                <Td right>
                  <OreField value={e.amount_ore} onCommit={(v) => loansApi.updateEntry(loan.id, e.id, { amount_ore: v }).then(reload)} />
                </Td>
                <TdNum strong>
                  <span className={e.running_balance_ore < 0 ? 'text-hv-chat-text' : 'text-hv-text'}>
                    {formatOre(e.running_balance_ore)}
                  </span>
                </TdNum>
                <Td>
                  <DeleteButton onClick={() => loansApi.deleteEntry(loan.id, e.id).then(reload)} />
                </Td>
              </Tr>
            ))}
            {entries.length === 0 && (
              <Tr>
                <Td colSpan={4}>
                  <span className="text-hv-text-tertiary">
                    No entries. Add a negative draw (e.g. −100,000) and later positive repayments.
                  </span>
                </Td>
              </Tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="border-t border-hv-border px-4 py-2 text-hv-xs text-hv-text-tertiary">
        Negative amount = a draw (debt up); positive amount = a repayment (debt down). The balance is recalculated by date.
      </div>
    </CollapsibleCard>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-hv-xs uppercase tracking-wide text-hv-text-tertiary">{label}</span>
      {children}
    </label>
  );
}
